import DOMPurify from 'dompurify'
import React, { useMemo, memo, useRef, useEffect } from 'react'
import mermaid from 'mermaid'
import 'highlight.js/styles/github.css'
import styles from '~/styles/MarkdownRenderer.module.css'
import { createMarkdownInstance } from './config/markdownConfig'
import { useSectionHandlers } from './hooks/useSectionHandlers'
import { TableRenderer, SectionRenderer } from './components'
import type { MarkdownRendererProps } from './types'

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  markdown,
  onSectionSelect,
  sectionStates = {},
  sectionReviews = {},
  processingStates = {}, // Add default value
  onAcceptReview,
  onRejectReview,  disableSections = false, // Default to false to maintain current behavior
  protectedSections = {}, // Default to empty object
  sectionMeta = {}, // Default to empty object
  sectionExplanations = {}, // Add default value
  sectionExplanationStates = {}, // Add default value for explanation states
  onToggleExplanation, // Add toggle handler
}) => {  // Use the extracted hooks
  const { selectedSection, handleSectionClick, formatDate } = useSectionHandlers(onSectionSelect)
  
  // Mermaid rendering state - moved back to main component for better control
  const renderingInProgress = useRef<Set<string>>(new Set())
  const mermaidCounter = useRef(0)
  
  // Store mermaid content mapping to avoid data attribute issues
  const mermaidContentMap = useRef<Map<string, string>>(new Map())

  // Initialize Mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      fontFamily: 'arial',
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true
      },
      logLevel: 'error'
    });
  }, []);  
    // Add useEffect to render Mermaid diagrams after component mounts/updates
  useEffect(() => {    
      const renderMermaidDiagrams = async () => {
      const mermaidElements = document.querySelectorAll('.mermaid-diagram[data-render-state="pending"]');
      console.log(`MarkdownRenderer: Found ${mermaidElements.length} pending mermaid diagrams to render`);
      
      if (mermaidElements.length === 0) {
        console.log('MarkdownRenderer: No pending mermaid diagrams found');
        return;
      }
      
      // Create a unique set of diagram IDs to avoid duplicate processing
      const processedIds = new Set<string>();
      
      for (const element of Array.from(mermaidElements)) {
        const htmlElement = element as HTMLElement;
        const diagramId = htmlElement.getAttribute('data-diagram-id');
        
        if (!diagramId || processedIds.has(diagramId)) {
          continue;
        }
        
        processedIds.add(diagramId);
        
        console.log('MarkdownRenderer: Processing mermaid element:', { 
          diagramId, 
          isConnected: htmlElement.isConnected,
          parentNode: htmlElement.parentNode?.nodeName,
          rect: htmlElement.getBoundingClientRect()
        });
        
        // Get content from the map instead of data attribute
        const mermaidContent = mermaidContentMap.current.get(diagramId);
        
        console.log('MarkdownRenderer: Retrieved content from map:', { diagramId, hasContent: !!mermaidContent });
        
        if (mermaidContent) {
          // Skip if already rendering
          if (renderingInProgress.current.has(diagramId)) {
            continue;
          }
          
          // Mark as rendering
          renderingInProgress.current.add(diagramId);
          htmlElement.setAttribute('data-render-state', 'rendering');
          
          try {
            console.log('MarkdownRenderer: Rendering Mermaid diagram:', diagramId);
            console.log('MarkdownRenderer: Mermaid content:', mermaidContent);
            
            // Generate unique render ID to avoid conflicts
            const renderId = `${diagramId}-render-${++mermaidCounter.current}`;
            console.log('MarkdownRenderer: Attempting to render with ID:', renderId);
            
            const { svg } = await mermaid.render(renderId, mermaidContent);
            
            // Find all elements with this diagram ID (in case there are multiple)
            const currentElements = document.querySelectorAll(`[data-diagram-id="${diagramId}"]`);
            
            if (currentElements.length > 0) {
              console.log(`MarkdownRenderer: Updating ${currentElements.length} element(s) with SVG for: ${diagramId}`);
              currentElements.forEach((currentElement) => {
                if (currentElement instanceof HTMLElement) {
                  currentElement.innerHTML = svg;
                  currentElement.setAttribute('data-render-state', 'rendered');
                }
              });
              console.log('MarkdownRenderer: DOM updated successfully for:', diagramId);
            } else {
              console.warn('MarkdownRenderer: No elements found for diagram ID:', diagramId);
            }
              console.log('MarkdownRenderer: Mermaid diagram rendered successfully:', diagramId);
          } catch (error) {
            console.error('Mermaid rendering error for', diagramId, ':', error);
            
            // Create a more user-friendly error message
            let errorMessage = 'Error rendering Mermaid diagram';
            let errorDetails = '';
            
            if (error instanceof Error) {
              const message = error.message;
              
              // Check for common syntax errors and provide helpful messages
              if (message.includes('Parse error')) {
                if (message.includes('got \'GRAPH\'') || message.includes('Expecting')) {
                  errorMessage = 'Mermaid Syntax Error';
                  errorDetails = 'It looks like there might be missing closing ``` markers or invalid syntax. Please check your mermaid diagram syntax.';
                } else {
                  errorMessage = 'Mermaid Parse Error';
                  errorDetails = 'There is a syntax error in your mermaid diagram. Please check the diagram syntax.';
                }
              } else if (message.includes('Lexical error')) {
                errorMessage = 'Mermaid Lexical Error';
                errorDetails = 'Invalid characters or structure in the mermaid diagram.';
              } else {
                errorMessage = 'Mermaid Error';
                errorDetails = message.length > 100 ? message.substring(0, 100) + '...' : message;
              }
            }
            
            // Find all elements with this diagram ID and show error
            const currentElements = document.querySelectorAll(`[data-diagram-id="${diagramId}"]`);
            currentElements.forEach((currentElement) => {
              if (currentElement instanceof HTMLElement) {
                currentElement.innerHTML = `
                  <div class="mermaid-error" style="
                    border: 2px solid #ff6b6b;
                    border-radius: 8px;
                    padding: 16px;
                    margin: 8px 0;
                    background-color: #fff5f5;
                    color: #c92a2a;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  ">
                    <div style="font-weight: 600; margin-bottom: 8px; display: flex; align-items: center;">
                      <span style="margin-right: 8px;">⚠️</span>
                      ${errorMessage}
                    </div>
                    <div style="font-size: 14px; line-height: 1.4;">
                      ${errorDetails}
                    </div>
                    <details style="margin-top: 8px;">
                      <summary style="cursor: pointer; color: #868e96; font-size: 12px;">Technical Details</summary>
                      <pre style="
                        background: #f8f9fa;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 4px;
                        font-size: 11px;
                        color: #495057;
                        white-space: pre-wrap;
                        word-wrap: break-word;
                      ">${error instanceof Error ? error.message : 'Unknown error'}</pre>
                    </details>
                  </div>`;
                currentElement.setAttribute('data-render-state', 'error');
              }
            });
          } finally {
            // Remove from rendering set
            renderingInProgress.current.delete(diagramId);
          }
        } else {
          console.warn('No content found in map for diagram ID:', diagramId);
        }
      }
    };// Small delay to ensure DOM is ready
    const timeoutId = setTimeout(renderMermaidDiagrams, 10);
    return () => clearTimeout(timeoutId);
  }, [markdown]); // Re-run when markdown changes
  // Clear rendering state when markdown changes significantly
  useEffect(() => {
    renderingInProgress.current.clear();
  }, [markdown]);  const components = useMemo(() => {
    if (!markdown) return []

    // Clear the content map for this render and reset counter
    mermaidContentMap.current.clear()
    
    // Create a markdown instance with the content map
    const markdownInstance = createMarkdownInstance(mermaidContentMap.current)
    
    const tokens = markdownInstance.parse(markdown, {})
    const result: (string | JSX.Element)[] = []
    let currentHtmlContent = ''
    let tableHeaders: string[] = []
    let tableRows: string[][] = []
    let inTable = false
    let inThead = false
    let currentRow: string[] = []
    let inSection = false
    let sectionTitle = ''
    let sectionContent = ''

    const flushHtmlContent = () => {
      if (currentHtmlContent) {
        // Configure DOMPurify to allow mermaid data attributes
        const cleanHtml = DOMPurify.sanitize(currentHtmlContent, {
          ADD_ATTR: ['data-diagram-id', 'data-render-state'],
          ADD_TAGS: ['div'],
          KEEP_CONTENT: true,
          ALLOW_DATA_ATTR: true
        });
        
        result.push(
          <div
            key={`html-${result.length}`}
            className="markdown-renderer"
            dangerouslySetInnerHTML={{
              __html: cleanHtml,
            }}
          />
        )
        currentHtmlContent = ''
      }
    }

    const flushSection = () => {
      if (sectionTitle && sectionContent) {
        if (disableSections) {
          // If sections are disabled, just render the content without section wrapper
          result.push(
            <div
              key={`section-content-${result.length}`}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(markdownInstance.render(sectionContent)),
              }}
            />
          )
        } else {
          // Generate consistent section IDs based on title to maintain identity across renders
          const sectionId = `section-${result.length + 1}`
          
          // Ensure we have a valid section title, never empty
          const normalizedSectionTitle = sectionTitle || 'Unnamed Section'

          const sectionState = sectionStates[sectionId] || 'none'
          const sectionReview = sectionReviews[sectionId]
          const isProcessing = processingStates[sectionId] || false
          const isProtected = protectedSections[sectionId] || protectedSections[normalizedSectionTitle] || false
          const sectionExplanation = sectionExplanations[sectionId]
          const isExplanationMinimized = sectionExplanationStates[sectionId] === false

          result.push(
            <SectionRenderer
              key={sectionId}
              sectionId={sectionId}
              sectionTitle={normalizedSectionTitle}
              sectionContent={sectionContent}
              sectionState={sectionState}
              sectionReview={sectionReview}
              isProcessing={isProcessing}
              isProtected={isProtected}
              sectionMeta={sectionMeta[sectionId]}
              sectionExplanation={sectionExplanation}
              isExplanationMinimized={isExplanationMinimized}
              selectedSection={selectedSection}
              onSectionClick={handleSectionClick}
              onAcceptReview={onAcceptReview}
              onRejectReview={onRejectReview}
              onToggleExplanation={onToggleExplanation}
              formatDate={formatDate}
              markdownInstance={markdownInstance}
            />
          )
        }
      }
      sectionTitle = ''
      sectionContent = ''
    }

    tokens.forEach((token, idx) => {
      switch (token.type) {
        case 'table_open':
          if (!inSection) {
            flushHtmlContent() // Only flush HTML when not in section
          }
          inTable = true
          break
        case 'thead_open':
          inThead = true
          break
        case 'thead_close':
          inThead = false
          break
        case 'tr_open':
          currentRow = []
          break
        case 'tr_close':
          if (inThead) {
            tableHeaders = currentRow
          } else if (inTable) {
            tableRows.push(currentRow)
          }
          break
        case 'th_open':
        case 'td_open':
          break
        case 'th_close':
        case 'td_close':
          break
        case 'container_section_open':
          flushHtmlContent() // Flush any content before starting section
          inSection = true
          // Extract section name from ::: section [Name]
          sectionTitle =
            (/^section\s+(.*)$/.exec(token.info
              .trim()))?.[1]
              ?.trim() || 'Unnamed Section'
          /* console.log(`Found section with title: "${sectionTitle}"`); */
          break
        case 'container_section_close':
          if (!disableSections) {
            inSection = false
            flushSection()
          }
          break
        case 'inline':
          if (inTable) {
            currentRow.push(token.content)
          } else {
            if (inSection) {
              sectionContent += markdownInstance.renderInline(token.content)
            } else {
              currentHtmlContent += markdownInstance.renderInline(token.content)
            }
          }
          break
        case 'table_close':
          inTable = false
          if (inSection) {
            // When inside a section, add table tokens to section content as raw markdown
            if (tableHeaders.length && tableRows.length) {
              let tableMarkdown = '\n\n'
              // Add header row
              tableMarkdown += '|' + tableHeaders.join('|') + '|\n'
              // Add separator row
              tableMarkdown += '|' + tableHeaders.map(() => '---').join('|') + '|\n'
              // Add data rows
              tableRows.forEach(row => {
                tableMarkdown += '|' + row.join('|') + '|\n'
              })
              tableMarkdown += '\n\n'
              sectionContent += tableMarkdown
            }
          } else {
            // When not in section, render table as React component immediately
            if (tableHeaders.length && tableRows.length) {
              result.push(
                <TableRenderer
                  key={`table-${idx}`}
                  headers={tableHeaders}
                  rows={tableRows}
                />
              )
            }
          }
          tableHeaders = []
          tableRows = []
          break
        default:
          if (!inTable) {
            if (inSection && !disableSections) {
              sectionContent += markdownInstance.renderer.render([token], markdownInstance.options, {})
            } else {
              currentHtmlContent += markdownInstance.renderer.render([token], markdownInstance.options, {})
            }
          }
          break
      }
    })

    // Flush any remaining HTML content
    flushHtmlContent()

    //console.log('redraw markdown')

    return result  }, [
    markdown,
    onSectionSelect,
    sectionStates,
    selectedSection,
    disableSections,
    sectionReviews,
    processingStates,
    protectedSections,
    sectionMeta,
    sectionExplanations,
    sectionExplanationStates,
    onAcceptReview,
    onRejectReview,
    onToggleExplanation,
    handleSectionClick,
    formatDate,
  ])

  return (
    <div className={styles['markdown-renderer-wrapper']}>
      {components.map((comp, index) => (
        <React.Fragment key={index}>{comp}</React.Fragment>
      ))}
    </div>
  )
}

export default memo(MarkdownRenderer)
